import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import { MultiValueRow, MultiValueRowID } from './multivalue-row';
import { UtilsService } from 'src/app/core/services/utils.service';
import { DPValidation } from 'src/app/core/models/DPValidation';
import { DPValidationType } from 'src/app/core/models/enums/DPValidationType';
import { DPValidatorsService } from 'src/app/core/services/dp-validators.service';

@Injectable({
  providedIn: 'root',
})
export class MultivalueService {
  private fieldRows: Record<string, BehaviorSubject<MultiValueRow[]>> = {};

  constructor(
    private utils: UtilsService,
    private validators: DPValidatorsService
  ) {}

  registerField(fieldId: string) {
    this.fieldRows[fieldId] = new BehaviorSubject([]);
  }

  getRowsObservable(fieldId: string) {
    return this.fieldRows[fieldId].asObservable();
  }

  getRows(fieldId: string) {
    return this.fieldRows[fieldId].getValue();
  }

  setRows(fieldId: string, rows: MultiValueRow[]) {
    this.fieldRows[fieldId].next(rows);
  }

  getRowById(fieldId: string, rowId: MultiValueRowID) {
    const rows = this.getRows(fieldId);
    return rows.find((row) => row.id === rowId);
  }

  addRow(fieldId: string, value: any) {
    const rows = this.getRows(fieldId);
    const newRow: MultiValueRow = {
      id: this.utils.generateID(),
      value,
    };

    this.setRows(fieldId, [...rows, newRow]);
  }

  updateRow(fieldId: string, updatedRow: MultiValueRow) {
    const rows = this.getRows(fieldId);
    const newRows = rows.map((row) =>
      row.id === updatedRow.id ? updatedRow : row
    );

    this.setRows(fieldId, newRows);
  }

  deleteRow(fieldId: string, rowId: string | number) {
    const rows = this.getRows(fieldId);
    const newRows = rows.filter((row) => row.id !== rowId);

    this.setRows(fieldId, newRows);
  }

  sortAndValidateRows(
    fieldId: string,
    startEndFieldName: string,
    validationRules: DPValidation[] | null
  ) {
    let rows = this.getRows(fieldId);
    rows = this.sortRows(rows, startEndFieldName);
    rows = this.addMissingGapRows(rows, validationRules);

    this.setRows(fieldId, rows);
  }

  updateRowsWithControlValue(
    fieldId: string,
    controlValue: string | null,
    startEndFieldName: string,
    validationRules: DPValidation[] | null
  ) {
    try {
      const rows = JSON.parse(controlValue || '[]') as any[];
      let rowsWithID = rows.map<MultiValueRow>((value) => {
        const id = this.utils.isNil(value?.id)
          ? this.utils.generateID()
          : value.id;
        return {
          id,
          value,
        };
      });
      rowsWithID = this.sortRows(rowsWithID, startEndFieldName);
      rowsWithID = this.addMissingGapRows(rowsWithID, validationRules);

      this.setRows(fieldId, rowsWithID);
    } catch {
      this.setRows(fieldId, []);
    }
  }

  getControlValueFromRows(fieldId: string) {
    const rows = this.getRows(fieldId);
    const values = rows.map((row) => row.value).filter((v) => !v.error);

    return JSON.stringify(values);
  }

  /**
   * Sort mutli-value entries using startEndDate field value.
   * @param startEndFieldName Start/End date field
   */
  sortRows(rows: MultiValueRow[], startEndFieldName: string | number) {
    if (startEndFieldName) {
      const newRows = rows.slice().filter((r) => !r.value.error);
      newRows.sort((row1, row2) => {
        const d1 = row1.value[startEndFieldName] || '';
        const d2 = row2.value[startEndFieldName] || '';
        const start1 = d1.split(' to ')[0];
        const start2 = d2.split(' to ')[0];
        return 1 - this.utils.compareDates(start1, start2);
      });
      return newRows;
    }

    return rows;
  }

  /**
   * Check missing gaps between multivalue rows and add the empty gap rows.
   * @param rows Multivalue rows
   * @param startEndFieldName Start/End field name
   * @returns Rows with empty gap fields if any
   */
  addMissingGapRows(rows: MultiValueRow[], validations: DPValidation[] | null) {
    /**
     * Get StartEndDateGap validation.
     * If the validation doesn't exist, don't check the gaps.
     */
    const rule = validations?.find(
      (r) => r.validationTypeId === DPValidationType.StartEndDateGap
    );
    if (!rule) {
      return rows;
    }

    /**
     * Calculate date segments based on current multi-value entries.
     *
     * Date segments are date ranges where the entries span:
     *
     * [ [startDate1, endDate1], [startDate2, endDate2], ... ]
     *
     */
    const dateSegments: Date[][] = [];
    for (let i = 0; i < rows.length; i++) {
      const strdates = rows[i].value[rule.pageQuestionLookupId].split(' to ');
      const dates = this.utils.normalizeStartEndDate(strdates);

      this.utils.pushDateSegment(dateSegments, dates);
    }

    const field = { validations } as any;

    /**
     * Truncates date segments later than now because we don't allow future dates in multivalue entries.
     */
    const pivot = this.utils.toDate(this.validators.getPivotDate(field));
    const newSegments1 = this.utils.getLimitedDateSegmentsByMaxDate(
      dateSegments,
      pivot
    );

    /**
     * Get startEndTotal value from validations.
     */
    const startEndTotal = this.validators.getStartEndTotalValue(field);
    let minDate: Date = null;
    if (startEndTotal) {
      /**
       * If startEndTotal validation exists, we should check only date segments later than "now - startEndDateTotal months".
       * Entries before that time will be ignored.
       */
      minDate = this.utils.toDate(this.validators.getPivotDate(field));
      minDate.setMonth(minDate.getMonth() - startEndTotal);
    }

    /**
     * Truncates date segments by the minDate calculated above.
     */
    const newSegments2 = minDate
      ? this.utils.getLimitedDateSegmentsByMinDate(newSegments1, minDate)
      : newSegments1;

    // Allowed gap value
    const allowedGap = +rule.value;

    // Multi-value entries displayed in the table.
    let newRows = rows.slice();

    /**
     * Check if gap between date segmenets is greater than the allowed gap.
     * If yes, add error gap entry to highlight the gap in the table.
     */
    for (let i = 0; i < newSegments2.length - 1; i++) {
      const endDate = newSegments2[i][1];
      const startDate = newSegments2[i + 1][0];
      const monthsDiff = this.utils.getMonthDiff1(endDate, startDate);
      if (monthsDiff > allowedGap) {
        newRows.push(
          this.gapEntry(rule.pageQuestionLookupId, endDate, startDate)
        );
      }
    }

    /**
     * If there are date segments to be checked,
     * check if there are gaps between last date segment to now or hireDate
     * and if there are gaps between minDate and first date segment
     * and add error gap entry as necessary.
     */
    if (newSegments2.length > 0) {
      const lastEndDate = newSegments2[newSegments2.length - 1][1];
      const gapToPivot = this.utils.getMonthDiff1(lastEndDate, pivot);
      if (gapToPivot > allowedGap) {
        newRows.push(
          this.gapEntry(rule.pageQuestionLookupId, lastEndDate, pivot)
        );
      }

      if (minDate) {
        const startDate = newSegments2[0][0];
        const gapToMin = this.utils.getMonthDiff1(minDate, startDate);

        if (gapToMin > allowedGap) {
          // The start date that is considered would be minDate + allowedGap (months).
          const minStartDate = new Date(minDate);

          newRows.push(
            this.gapEntry(rule.pageQuestionLookupId, minStartDate, startDate)
          );
        }
      }
    } else if (minDate) {
      /**
       * If there are no valid date segments and minDate exists,
       * add error gap entry spaning minDate to now or hiredDate.
       */
      newRows.push(this.gapEntry(rule.pageQuestionLookupId, minDate, pivot));
    }

    /**
     * Sort gap entries by startEndDate field.
     */
    newRows.sort((row1, row2) => {
      const d1 = row1.value[rule.pageQuestionLookupId] || '';
      const d2 = row2.value[rule.pageQuestionLookupId] || '';
      const start1 = d1.split(' to ')[0];
      const start2 = d2.split(' to ')[0];
      return -this.utils.compareDates(start1, start2);
    });

    return newRows;
  }

  /**
   * Add error gap entry.
   * @param fieldId Field id
   * @param from Start date of gap
   * @param to End date of gap
   * @returns Gap entry object
   */
  private gapEntry(fieldId: number | string, from: Date, to: Date) {
    const fromStr = this.utils.getDateString(from);
    const toStr = this.utils.getDateString(to);
    const startEndDate = `${fromStr} to ${toStr}`;

    if (from > to) {
      return {
        id: this.utils.generateID(),
        value: {
          error: 'Please correct your birth date',
          fieldName: '',
          startEndDate,
          [fieldId]: startEndDate,
        },
      } as MultiValueRow;
    }

    return {
      id: this.utils.generateID(),
      value: {
        error: `Missing employment from ${startEndDate}`,
        fieldName: `${fieldId}`,
        startEndDate,
        [fieldId]: startEndDate,
      },
    } as MultiValueRow;
  }
}
